import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icons";

/* ---------- Status Badge ---------- */
const STATUS_STYLES: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-300",
  provisioning: "bg-orange-100 text-orange-800 border-orange-300",
  suspended: "bg-red-100 text-red-800 border-red-300",
  archived: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_DOTS: Record<string, string> = {
  active: "bg-green-500",
  provisioning: "bg-orange-500",
  suspended: "bg-red-500",
  archived: "bg-slate-400",
};

export function StatusBadge({
  status,
  withDot = true,
}: {
  status: string;
  withDot?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize",
        STATUS_STYLES[status],
      )}
    >
      {withDot && (
        <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOTS[status])} />
      )}
      {status}
    </span>
  );
}

/* ---------- Deploy Badge ---------- */
const DEPLOY_STYLES: Record<string, string> = {
  deployed: "bg-green-100 text-green-800 border-green-300",
  deploying: "bg-blue-100 text-blue-800 border-blue-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  draft: "bg-slate-100 text-slate-600 border-slate-300",
};

export function DeployBadge({ status }: { status: string | undefined | null }) {
  const s = status || "draft";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize",
        DEPLOY_STYLES[s],
      )}
    >
      {s === "deploying" && (
        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
      )}
      {s}
    </span>
  );
}

/* ---------- Category Badge ---------- */
const CAT_STYLES: Record<string, string> = {
  core: "bg-blue-100 text-blue-700",
  addon: "bg-amber-100 text-amber-700",
  premium: "bg-purple-100 text-purple-700",
};

export function CatBadge({ category }: { category: string }) {
  return (
    <span
      className={cn(
        "rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize",
        CAT_STYLES[category],
      )}
    >
      {category}
    </span>
  );
}

/* ---------- Trial Badge ---------- */
function getTrial(tenant: { trial_ends_at?: string | null }) {
  if (!tenant.trial_ends_at)
    return { inTrial: false, daysLeft: 0, expired: false };
  const end = new Date(tenant.trial_ends_at + "T23:59:59");
  const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
  return { inTrial: true, daysLeft: days, expired: days <= 0 };
}

export function TrialBadge({
  tenant,
  className = "",
}: {
  tenant: { trial_ends_at?: string | null };
  className?: string;
}) {
  const tr = getTrial(tenant);
  if (!tr.inTrial) return null;
  const tone =
    tr.expired || tr.daysLeft <= 3
      ? "border-red-300 bg-red-100 text-red-700"
      : tr.daysLeft <= 7
        ? "border-orange-300 bg-orange-100 text-orange-700"
        : "border-blue-300 bg-blue-100 text-blue-700";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-none",
        tone,
        className,
      )}
    >
      <Icon
        name={tr.daysLeft <= 3 && !tr.expired ? "alert" : "clock"}
        size={10}
      />
      {tr.expired ? "Trial ended" : `Trial · ${tr.daysLeft}d left`}
    </span>
  );
}

/* ---------- Origin Badge ---------- */
export function OriginBadge({
  tenant,
  className = "",
}: {
  tenant: { created_via?: string };
  className?: string;
}) {
  const self = tenant.created_via === "self_trial";
  const label = self ? "Self-serve" : "Admin-created";
  return (
    <span
      title={self ? "Signed up via the website" : "Created by ops staff"}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-semibold leading-none",
        self
          ? "border-blue-200 bg-blue-50 text-blue-700"
          : "border-border bg-muted/60 text-muted-foreground",
        className,
      )}
    >
      <Icon name={self ? "globe" : "shield"} size={10} />
      {label}
    </span>
  );
}

export { getTrial };
